/* CLOWN · Animations d'entrée communes. Mode d'emploi en tête de clown-motion.css. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduced.matches || !('IntersectionObserver' in window)) return;
  const root = document.documentElement;
  root.classList.add('cr-motion');
  const pending = new Set();

  // Regroupe les nœuds du titre entre les <br> dans des lignes masquées, sans réécrire le HTML.
  const splitLines = el => {
    if (el.dataset.animSplit) return;
    el.dataset.animSplit = 'true';
    const lines = [[]];
    [...el.childNodes].forEach(node => {
      if (node.nodeName === 'BR') lines.push([]);
      else lines[lines.length - 1].push(node);
    });
    el.replaceChildren(...lines.filter(nodes => nodes.some(n => n.textContent.trim())).map((nodes, i) => {
      const line = document.createElement('span');
      const inner = document.createElement('span');
      line.className = 'cr-anim-line';
      line.style.setProperty('--line-i', i);
      inner.append(...nodes);
      line.append(inner);
      return line;
    }));
  };

  const count = el => {
    const target = el.dataset.animCountTo || el.textContent.trim();
    const match = target.match(/(\d+(?:[.,]\d+)?)/);
    if (!match) return;
    const raw = match[1];
    const sep = raw.includes(',') ? ',' : '.';
    const decimals = raw.includes(sep) ? raw.split(sep)[1].length : 0;
    const end = parseFloat(raw.replace(',', '.'));
    const before = target.slice(0, match.index);
    const after = target.slice(match.index + raw.length);
    const start = performance.now();
    const duration = 1400;
    el.dataset.animCountTo = target;
    const tick = now => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 4);
      el.textContent = before + (end * eased).toFixed(decimals).replace('.', sep) + after;
      if (t < 1) requestAnimationFrame(tick);
      else el.textContent = target;
    };
    requestAnimationFrame(tick);
  };

  const show = el => {
    if (root.classList.contains('cr-intro-pending')) { pending.add(el); return; }
    if (el.classList.contains('is-in')) return;
    el.classList.add('is-in');
    if (el.hasAttribute('data-anim-count')) count(el);
    el.querySelectorAll('[data-anim-count]:not([data-anim])').forEach(count);
  };
  // The hero starts as the logo curtain opens, never invisibly behind it.
  document.addEventListener('clown:intro:end', () => {
    requestAnimationFrame(() => {
      pending.forEach(show);
      pending.clear();
    });
  });

  // Une cible entièrement découpée par clip-path (mask, wipe) est vue comme invisible
  // par IntersectionObserver : on observe alors son parent (ancre) et on relie les deux.
  const clipped = new Map();
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      show(entry.target);
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
  // Ancres : seuil 0 (un parent très haut sur mobile n'atteint jamais 12 %).
  const anchorObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      anchorObserver.unobserve(entry.target);
      (clipped.get(entry.target) || []).forEach(show);
      clipped.delete(entry.target);
    });
  }, { threshold: 0, rootMargin: '0px 0px -10% 0px' });
  const group = (anchor, el) => {
    if (!clipped.has(anchor)) { clipped.set(anchor, []); anchorObserver.observe(anchor); }
    clipped.get(anchor).push(el);
  };
  // Carrousel horizontal : tout ce qu'il contient entre en même temps que lui,
  // sinon les cartes hors écran à droite resteraient cachées jusqu'au balayage.
  const scrollerOf = el => {
    for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
      const overflow = getComputedStyle(node).overflowX;
      // Sans test de scrollWidth : la piste peut ne déborder qu'après le chargement des images.
      if (overflow === 'auto' || overflow === 'scroll') return node;
    }
    return null;
  };
  const watch = el => {
    const scroller = scrollerOf(el);
    if (scroller) return group(scroller, el);
    if (['mask', 'wipe'].includes(el.dataset.anim)) {
      // Premier ancêtre qui a une boîte (display:contents n'en a pas).
      let anchor = el.parentElement;
      while (anchor && anchor !== document.body && getComputedStyle(anchor).display === 'contents') anchor = anchor.parentElement;
      return anchor ? group(anchor, el) : show(el);
    }
    // Bloc plus haut que l'écran : 12 % de sa hauteur ne serait jamais visible d'un coup.
    if (el.offsetHeight > window.innerHeight * 0.8) return group(el, el);
    observer.observe(el);
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-anim-stagger]').forEach(parent => {
      const step = parseFloat(parent.dataset.animStagger) || 0.09;
      const base = parseFloat(getComputedStyle(parent).getPropertyValue('--anim-delay')) || 0;
      [...parent.children].forEach((child, i) => {
        if (!child.hasAttribute('data-anim')) child.setAttribute('data-anim', parent.dataset.animChild || 'up');
        child.style.setProperty('--anim-delay', `${(base + Math.min(i, 5) * step).toFixed(2)}s`);
      });
    });
    scope.querySelectorAll('[data-anim]').forEach(el => {
      if (el.dataset.animReady) return;
      el.dataset.animReady = 'true';
      if (el.dataset.anim === 'lines') splitLines(el);
      if (el.hasAttribute('data-anim-load') || el.closest('[data-anim-load]')) {
        requestAnimationFrame(() => requestAnimationFrame(() => show(el)));
      } else {
        watch(el);
      }
    });
    scope.querySelectorAll('[data-anim-count]:not([data-anim])').forEach(el => {
      if (el.dataset.animReady || el.closest('[data-anim]')) return;
      el.dataset.animReady = 'true';
      const io = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { io.disconnect(); count(el); } }, { threshold: 0.6 });
      io.observe(el);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  // Keyboard navigation must never focus content while it is still visually concealed.
  document.addEventListener('focusin', event => {
    for (let el = event.target; el && el !== document.body; el = el.parentElement) {
      if (el.hasAttribute?.('data-anim')) show(el);
    }
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) document.querySelectorAll('[data-anim]').forEach(show);
  });
  reduced.addEventListener('change', () => {
    if (!reduced.matches) return;
    root.classList.remove('cr-motion');
    document.querySelectorAll('[data-anim]').forEach(el => el.classList.add('is-in'));
  });
  window.ClownMotion = { init };
})();
